import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react';
import { CalendarDropdown, CalendarHeader, Container, DayCell, DaysGrid, NavButton, Trigger, WeekDaysRow } from './index.styles';
import { Icon } from '../../atoms/Icon';

const WEEK_DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const toISODate = (d: Date): string => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');

  return `${yyyy}-${mm}-${dd}`;
};

const parseDate = (s: string): Date => {
  const [y, m, d] = s.split('-').map(Number);

  return new Date(y, m - 1, d);
};

const isSameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const getMonthLabel = (year: number, month: number, locale: string): string => {
  const d = new Date(year, month, 1);

  return d.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
};

const getCalendarDays = (year: number, month: number): Date[] => {
  const firstDay = new Date(year, month, 1);
  const startOffset = firstDay.getDay();
  const days: Date[] = [];

  for (let i = -startOffset; days.length < 42; i++) {
    days.push(new Date(year, month, 1 + i));
  }

  return days;
};

export interface DatePickerProps {
  className?: string;
  customClass?: string;
  defaultOpen?: boolean;
  disabled?: boolean;
  endDate?: string;
  locale?: string;
  maxRangeDays?: number;
  onChange: (startDate: string, endDate: string) => void;
  placeholder?: string;
  portalDomId?: string;
  startDate?: string;
  mode?: 'free' | 'past' | 'future';
  testId?: string;
}

export const DatePicker = (props: DatePickerProps) => {
  const {
    className = '',
    customClass = '',
    defaultOpen = false,
    disabled = false,
    endDate,
    locale = 'en-US',
    maxRangeDays,
    mode = 'free',
    onChange,
    placeholder = 'Select date range',
    portalDomId = 'dropdown-root',
    startDate,
    testId = 'date-picker',
  } = props;

  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [viewYear, setViewYear] = useState(() => (startDate ? parseDate(startDate).getFullYear() : new Date().getFullYear()));
  const [viewMonth, setViewMonth] = useState(() => (startDate ? parseDate(startDate).getMonth() : new Date().getMonth()));
  const [selectionStart, setSelectionStart] = useState<Date | null>(() => (startDate ? parseDate(startDate) : null));
  const [selectionEnd, setSelectionEnd] = useState<Date | null>(() => (endDate ? parseDate(endDate) : null));
  const [pickingEnd, setPickingEnd] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // 1. Clean React 19 Fix: Handle tracking prop synchronization dynamically during render
  // instead of catching it late inside a late-mounted useEffect block.
  const [prevProps, setPrevProps] = useState({ startDate, endDate });

  if (prevProps.startDate !== startDate || prevProps.endDate !== endDate) {
    setPrevProps({ startDate, endDate });
    if (startDate) {
      const d = parseDate(startDate);

      setSelectionStart(d);
      setViewYear(d.getFullYear());
      setViewMonth(d.getMonth());
    } else {
      setSelectionStart(null);
    }
    setSelectionEnd(endDate ? parseDate(endDate) : null);
  }

  const { x, y, strategy, refs } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'bottom-start',
    middleware: [offset(4), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });

  const today = useMemo(() => {
    const d = new Date();

    d.setHours(0, 0, 0, 0);

    return d;
  }, []);

  const days = useMemo(() => getCalendarDays(viewYear, viewMonth), [viewYear, viewMonth]);
  const monthLabel = useMemo(() => getMonthLabel(viewYear, viewMonth, locale), [viewYear, viewMonth, locale]);

  const handlePrevMonth = useCallback(() => {
    setViewMonth((m) => {
      if (m === 0) {
        setViewYear((prev) => prev - 1);

        return 11;
      }

      return m - 1;
    });
  }, []);

  const handleNextMonth = useCallback(() => {
    setViewMonth((m) => {
      if (m === 11) {
        setViewYear((prev) => prev + 1);

        return 0;
      }

      return m + 1;
    });
  }, []);

  const isDayDisabled = useCallback(
    (day: Date): boolean => {
      if (mode === 'past' && day > today) return true;
      if (mode === 'future' && day < today) return true;

      if (!maxRangeDays || !pickingEnd || !selectionStart) return false;

      const diffMs = Math.abs(day.getTime() - selectionStart.getTime());
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      return diffDays > maxRangeDays;
    },
    [maxRangeDays, pickingEnd, selectionStart, mode, today]
  );

  const handleDayClick = useCallback(
    (day: Date) => {
      if (isDayDisabled(day)) return;

      if (!pickingEnd || !selectionStart) {
        setSelectionStart(day);
        setSelectionEnd(null);
        setPickingEnd(true);
      } else {
        let start = selectionStart;
        let end = day;

        if (end < start) {
          [start, end] = [end, start];
        }

        setSelectionStart(start);
        setSelectionEnd(end);
        setPickingEnd(false);
        setIsOpen(false);
        onChange(toISODate(start), toISODate(end));
      }
    },
    [pickingEnd, selectionStart, onChange, isDayDisabled]
  );

  const isInRange = useCallback(
    (day: Date): boolean => {
      if (!selectionStart || !selectionEnd) return false;

      return day > selectionStart && day < selectionEnd;
    },
    [selectionStart, selectionEnd]
  );

  const displayValue = useMemo(() => {
    if (!selectionStart) return '';

    const formatDate = (d: Date) =>
      d.toLocaleDateString(locale, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });

    if (!selectionEnd) return formatDate(selectionStart);

    return `${formatDate(selectionStart)} - ${formatDate(selectionEnd)}`;
  }, [selectionStart, selectionEnd, locale]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inContainer = containerRef.current?.contains(target);
      const inDropdown = dropdownRef.current?.contains(target);

      if (!inContainer && !inDropdown) {
        setIsOpen(false);
        setPickingEnd(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        setPickingEnd(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside, true);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  // 2. Clean React 19 Fix: Defer closing state callback modifications to queueMicrotask
  // to prevent cascading synchronous render blocks during click-away phases.
  useEffect(() => {
    if (!isOpen && pickingEnd && selectionStart) {
      queueMicrotask(() => {
        setSelectionEnd(selectionStart);
        setPickingEnd(false);
        onChange(toISODate(selectionStart), toISODate(selectionStart));
      });
    }
  }, [isOpen, pickingEnd, selectionStart, onChange]);

  const portalEl = typeof document !== 'undefined' ? document.getElementById(portalDomId) : null;

  return (
    <Container
      className={`date-picker ${customClass} ${className}`}
      data-testid={testId}
      ref={containerRef}
    >
      <Trigger
        $hasValue={!!displayValue}
        data-testid={`${testId}-trigger`}
        disabled={disabled}
        onClick={() => !disabled && setIsOpen((prev) => !prev)}
        ref={(node) => refs.setReference(node)}
        type="button"
      >
        <Icon
          color="brandDark"
          name="calendar"
        />
        <span>{displayValue || placeholder}</span>
      </Trigger>

      {isOpen &&
        portalEl &&
        createPortal(
          <CalendarDropdown
            data-testid={`${testId}-dropdown`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            ref={(node) => {
              refs.setFloating(node);
              dropdownRef.current = node;
            }}
            style={{ position: strategy, top: y ?? 0, left: x ?? 0 }}
          >
            <CalendarHeader>
              <NavButton
                data-testid={`${testId}-prev`}
                onClick={handlePrevMonth}
                type="button"
              >
                <Icon
                  color="brandDark"
                  name="chevron_left"
                />
              </NavButton>
              <span data-testid={`${testId}-month-label`}>{monthLabel}</span>
              <NavButton
                data-testid={`${testId}-next`}
                onClick={handleNextMonth}
                type="button"
              >
                <Icon
                  color="brandDark"
                  name="chevron_right"
                />
              </NavButton>
            </CalendarHeader>

            <WeekDaysRow>
              {WEEK_DAYS.map((d) => (
                <span key={d}>{d}</span>
              ))}
            </WeekDaysRow>

            <DaysGrid>
              {days.map((day, i) => {
                const isCurrentMonth = day.getMonth() === viewMonth;
                const isSelectedStart = !!selectionStart && isSameDay(day, selectionStart);
                const isSelectedEnd = !!selectionEnd && isSameDay(day, selectionEnd);
                const isSelected = isSelectedStart || isSelectedEnd;
                const inRange = isInRange(day);
                const isDisabled = isDayDisabled(day);

                return (
                  <DayCell
                    $isCurrentMonth={isCurrentMonth}
                    $isDisabled={isDisabled}
                    $isInRange={inRange}
                    $isRangeEnd={isSelectedEnd && !!selectionStart}
                    $isRangeStart={isSelectedStart && !!selectionEnd}
                    $isSelected={isSelected}
                    $isToday={isSameDay(day, today)}
                    data-testid={`${testId}-day-${toISODate(day)}`}
                    key={`${toISODate(day)}-${i}`}
                    onClick={() => handleDayClick(day)}
                    type="button"
                  >
                    {day.getDate()}
                  </DayCell>
                );
              })}
            </DaysGrid>
          </CalendarDropdown>,
          portalEl
        )}
    </Container>
  );
};
